import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';

import EmptyState from '../../DesignSystem/EmptyState';
import CustomIcon from '../../DesignSystem/CustomIcon';
import { icons } from '../../Posts/Forms/Common/FormBuilder/Inputs/TypeSelect';
import { Icons } from '../../../Themes';
import useIntersectionObserver from '../../../Hooks/useIntersectionObserver';
import useApiQuery from '../../../Hooks/useApiQuery';
import queriesConfig from '../../../Services/queriesConfig';
import QuestionOptions from './QuestionOptions';
import Answers from './Answers';

const IconWrapper = styled('span')({
  width: '40px',
  '& svg': {
    verticalAlign: 'bottom',
  },
});

function Question({ index, post, question, height = 350 }: any) {
  const intl = useIntl();
  const myRef = useRef<HTMLElement>(null);
  const isSectionVisible = useIntersectionObserver(myRef);
  const icon = icons[question?.field_type as keyof typeof icons];

  const { data: results, isLoading } = useApiQuery({
    query: queriesConfig.getQuestionResults(question?.id),
    options: {
      enabled: isSectionVisible,
    },
  });

  const noDataToRender =
    !isLoading && results?.question_options?.length === 0 && !results?.answers.length === 0;

  return (
    <section ref={myRef}>
      <Box sx={{ display: 'flex' }}>
        <IconWrapper>{icon}</IconWrapper>
        <Typography sx={{ fontWeight: 'bold', marginInlineEnd: 1 }}>{index + 1}.</Typography>

        <Box>
          <Typography component="h2" sx={{ fontWeight: 'bold' }}>
            {question?.title}
          </Typography>

          <Typography sx={{ color: 'text.secondary' }}>
            {intl.formatMessage(
              { id: 'STATS.TOTAL_ANSWERS' },
              {
                answersCount: get(results, 'answers_count', 0),
                totalCount: get(post, 'post_contributions_stats.form_submissions_count', 0),
              },
            )}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 8, py: 5 }}>
        {!isLoading && noDataToRender && (
          <EmptyState
            icon={<CustomIcon src={Icons.misc.searchOff} color="primary" size={30} />}
            content={
              <Typography>
                <FormattedMessage id="STATS.NO_DATA" />
              </Typography>
            }
          />
        )}

        {isLoading && <Skeleton variant="rectangular" height={height} width="100%" />}

        {results?.question_options?.length > 0 && (
          <QuestionOptions questionId={question.id} questionOptions={results?.question_options} />
        )}

        {results?.answers?.length > 0 && (
          <Answers questionId={question.id} answers={results?.answers} />
        )}
      </Box>
    </section>
  );
}

export default Question;
